import React, { useEffect, useRef, useState } from 'react';
import Modal, { MODAL_WELL, MODAL_ERROR, MODAL_NOTE, modalRow } from '../Modal';
import Button from '../Button';
import CardPaymentSection from '../CardPaymentSection';
import DevCardElement from '../DevCardElement';
import { plannerService, stripeService } from '../../services';
import { isStripeDevMode, mockConfirmCardSetup } from '../../utils/stripeDevMode';

const money = (cents) => `$${(cents / 100).toFixed(0)}`;

const TIERS = [
  { key: 'host_pro', name: 'HOST PRO', line: 'Unlimited plans and revisions, permit reminders, vendor roster. Poster on approve.' },
  { key: 'ultimate', name: 'PARTY PLANNER ULTIMATE', line: 'Everything in Host Pro, plus the poster on the first pass and every revision.' },
  { key: 'planner_pro', name: 'PLANNER PRO', line: 'Ultimate for a team: multi-client workspaces, white-label share cards, 5 seats.' },
];

/**
 * Planner tiers. Same card-capture flow as the Pro subscription modal. Admins never see this
 * — they're Ultimate for free and the planner page never offers them an upgrade.
 */
export default function PlannerBillingModal({ isOpen, onClose, billing, initialTier = 'ultimate', onDone }) {
  const [tier, setTier] = useState(initialTier);
  const [cardReady, setCardReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [devBehavior, setDevBehavior] = useState('success');
  const cardRef = useRef(null);
  useEffect(() => { setTier(initialTier); }, [initialTier, isOpen]);
  const prices = billing?.prices || { host_pro: 2900, ultimate: 7900, planner_pro: 14900 };

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      let pmId; let customerId = null;
      if (isStripeDevMode()) {
        const mock = await mockConfirmCardSetup(devBehavior);
        if (mock.error) { setError(mock.error.message); return; }
        pmId = mock.setupIntent.payment_method; customerId = `cus_dev_${Date.now()}`;
      } else {
        const setup = await stripeService.createSetupIntent();
        if (!setup.success) { setError(setup.error || 'Could not prepare payment.'); return; }
        const res = await cardRef.current?.confirmSetup(setup.data.clientSecret, {});
        if (!res || res.error) { setError(res?.error || 'Card form is not ready.'); return; }
        pmId = res.paymentMethod; customerId = setup.data.customerId;
      }
      const r = await plannerService.startBilling({ tier, stripePaymentMethodId: pmId, stripeCustomerId: customerId });
      if (r.success) { onDone?.(r.data); onClose(); } else setError(r.error || 'Could not start your subscription.');
    } finally { setSubmitting(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Planner tiers" size="md">
      <div className="space-y-3 font-mono">
        {TIERS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTier(t.key)} aria-pressed={tier === t.key}
            className={modalRow(tier === t.key)}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider">{t.name}</span>
              <span className="text-lg font-bold">{money(prices[t.key])}<span className="text-xs opacity-70"> / mo</span></span>
            </div>
            <p className="text-[11px] mt-1 opacity-80">{t.line}</p>
          </button>
        ))}
        <p className={MODAL_NOTE}>Each tier's planning budget is set so it always covers what it costs us to run. Cancel anytime — you keep the month you paid for.</p>
        <div className={MODAL_WELL}>
          {isStripeDevMode()
            ? <DevCardElement value={devBehavior} onChange={setDevBehavior} />
            : <CardPaymentSection ref={cardRef} amountCents={prices[tier]} currency="cad" countryDefault="CA" onValidityChange={setCardReady} disabled={submitting} label="PAY WITH CARD" />}
        </div>
        {error && <div className={MODAL_ERROR} role="alert">{error}</div>}
        <Button fullWidth onClick={submit} disabled={submitting || (!isStripeDevMode() && !cardReady)}>
          {submitting ? 'STARTING…' : `SUBSCRIBE · ${money(prices[tier])}/MO`}
        </Button>
      </div>
    </Modal>
  );
}
